/**
 * tierLimits — canonical copy for every "this needs a higher plan" wall.
 *
 * The server gates features/quotas with codes like `feature_locked`,
 * `jira_project_limit_reached`, `storage_quota_exceeded`. A single code
 * (`feature_locked`) fronts several distinct features (Explore, AI field
 * suggestions, connector write-back…), each of which wants its own copy — so we
 * key this registry by a LOGICAL limit key the call site chooses, not by the raw
 * wire code.
 *
 * Per the tier-surfaces-are-conversion-levers rule, every entry states three
 * things the old "upgrade your plan" toast did not: WHAT is blocked, WHAT
 * unlocks it (the named tier), and — via TierLimitModal's button — one click to
 * billing. Keep all upgrade copy here, never inline in a catch block, so a
 * pricing/tier change is a one-file edit. See [[project-blocking-modal-pattern]].
 */

export type TierLimitKey =
  | 'explore'
  | 'aiSuggestions'
  | 'aiAutoMap'
  | 'asanaWriteBack'
  | 'azureWriteBack'
  | 'jiraProjectLimit'
  | 'storageQuota';

export interface TierLimitCopy {
  /** Header line — what surface is gated. */
  title: string;
  /** One sentence: what the user just tried that's gated. */
  what: string;
  /** One sentence: which plan unlocks it (named tier). */
  unlocks: string;
}

export const TIER_LIMIT_COPY: Record<TierLimitKey, TierLimitCopy> = {
  explore: {
    title: 'Explore is a higher-plan feature',
    what: 'Avium Intelligence Explore answers free-form questions across your delivery data.',
    unlocks: 'Available on the Team plan and above.',
  },
  aiSuggestions: {
    title: 'AI field suggestions need a higher plan',
    what: 'AI-suggested field mappings match your source columns to Avium fields automatically.',
    unlocks: 'Available on the Team plan and above.',
  },
  aiAutoMap: {
    title: 'AI auto-map needs a higher plan',
    what: 'AI auto-map matches your import columns to Avium fields for you.',
    unlocks: 'Available on the Team plan and above. You can still map columns manually.',
  },
  asanaWriteBack: {
    title: 'Asana write-back needs a higher plan',
    what: 'Pushing changes back to Asana keeps your source of truth in sync from Avium.',
    unlocks: 'Available on the Business plan and above.',
  },
  azureWriteBack: {
    title: 'Azure DevOps write-back needs a higher plan',
    what: 'Pushing changes back to Azure DevOps keeps your source of truth in sync from Avium.',
    unlocks: 'Available on the Business plan and above.',
  },
  jiraProjectLimit: {
    title: 'Project limit reached',
    what: "You've reached the number of Jira projects your plan can sync.",
    unlocks: 'Upgrade your plan to connect more projects.',
  },
  storageQuota: {
    title: 'Storage is full',
    what: "Your organization has used all the file storage included in your plan.",
    unlocks: 'Upgrade your plan for more storage, or remove files you no longer need.',
  },
};
